
import { defineComponent, ref, computed, Ref, CSSProperties } from 'vue';
import outsideClickDirective from '@/helpers/directives/outside-click';
import AcreImage from '../AcreImage/AcreImage.vue';

export default defineComponent({
  name: 'AcreTooltip',
  components: { AcreImage },
  directives: {
    outsideClick: outsideClickDirective,
  },
  props: {
    contentWidth: {
      type: String,
      default: '100px',
    },
    contentIcon: {
      default: '',
      type: String,
    },
    activatorBordered: {
      default: false,
      type: Boolean,
    },
    openOnClick: {
      default: false,
      type: Boolean,
    },
    helpCursor: {
      default: false,
      type: Boolean,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
  },
  setup(props) {
    const activatorRef: Ref<null | HTMLElement> = ref(null);
    const contentRef: Ref<null | HTMLElement> = ref(null);
    const isActive = ref(false);
    const isShow = ref(false);
    const contentOffsetLeft = ref(0);
    const arrowOffsetLeft = ref(0);
    const side: Ref<'top'|'bottom'> = ref('top');

    function calculateOffsetLeft() {
      if (!activatorRef.value || !contentRef.value) return;
      const viewportWidth = document.documentElement.clientWidth;

      const contentWidth = contentRef.value?.offsetWidth || 0;

      const activatorWidth = activatorRef.value?.offsetWidth || 0;
      const activatorRect = activatorRef.value?.getBoundingClientRect();
      const activatorLeft = activatorRect.left || 0;
      const activatorRight = activatorRect.right || 0;

      let offsetLeft = ((activatorWidth - contentWidth) / 2);

      // check if content is outside viewport on right side
      if (activatorRight + Math.abs(offsetLeft) + 15 > viewportWidth) {
        offsetLeft = (viewportWidth - activatorLeft - 15) - contentWidth;
      // check if content is outside viewport on left side
      } else if (Math.abs(offsetLeft + 15) > activatorLeft) {
        offsetLeft = 15 - activatorLeft;
      }

      contentOffsetLeft.value = offsetLeft;
    }

    function calculateArrowLeft() {
      const activatorWidth = activatorRef.value?.offsetWidth || 0;
      arrowOffsetLeft.value = Math.abs(contentOffsetLeft.value) + (activatorWidth / 2);
    }

    function calculateSide() {
      const activatorRect = activatorRef.value?.getBoundingClientRect();
      const contentRect = contentRef.value?.getBoundingClientRect();
      if (!activatorRect || !contentRect) return;
      side.value = activatorRect.top < (contentRect.height + 80) ? 'bottom' : 'top';
    }

    const contentStyles = computed((): CSSProperties => {
      // we add props.contentWidth + 2 -> 2px to include border
      const styles: CSSProperties = { width: `calc(${props.contentWidth} + 2px)`, maxWidth: 'calc(100vw - 30px)' };
      if (contentOffsetLeft.value) {
        styles.left = `${contentOffsetLeft.value}px`;
      }
      if (arrowOffsetLeft.value) {
        styles['--arrow-left'] = `${arrowOffsetLeft.value}px`;
      }
      return styles;
    });

    async function onMouseEnter() {
      if (props.disabled) return;
      isActive.value = true;
      setTimeout(() => {
        calculateOffsetLeft();
        calculateSide();
        calculateArrowLeft();
        isShow.value = true;
      });
    }

    function onMouseLeave() {
      isActive.value = false;
      isShow.value = false;
    }

    function onClick() {
      if (props.openOnClick) onMouseEnter();
    }

    function onHoverEnter() {
      if (!props.openOnClick) onMouseEnter();
    }

    function onHoverLeave() {
      if (!props.openOnClick) onMouseLeave();
    }

    return {
      isActive,
      isShow,
      activatorRef,
      contentRef,
      contentOffsetLeft,
      contentStyles,
      side,
      onMouseLeave,
      onClick,
      onHoverEnter,
      onHoverLeave,
    };
  },
});
